package com.siriusxm.pia.views.unifiedaggregator.events

import com.siriusxm.pia.rest.unifiedaggregator.diffJson
import com.siriusxm.pia.rest.unifiedaggregator.entityId
import com.siriusxm.pia.rest.unifiedaggregator.entityType
import com.siriusxm.pia.rest.unifiedaggregator.entityVersion
import com.siriusxm.pia.rest.unifiedaggregator.FieldDiff
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.formatFieldName
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject

/**
 * Result of comparing a rejected entity with its authorized version.
 *
 * @property rejected The rejected entity data from the error event
 * @property authorized The currently authorized/published version of the entity, or null if none exists
 * @property referenceEntity A valid entity of the same type, shown when no authorized version exists
 * @property entityId The entity ID
 * @property entityType The entity type (e.g., "track", "album")
 * @property entityVersion The entity version (e.g., "0.1.0")
 * @property diffs List of field differences between authorized and rejected versions
 */
data class EntityComparisonResult(
    val rejected: JsonObject,
    val authorized: JsonObject?,
    val referenceEntity: JsonObject? = null,
    val entityId: String,
    val entityType: String,
    val entityVersion: String,
    val diffs: List<FieldDiff>
)

/**
 * Compares a rejected entity update with the currently authorized version.
 *
 * Extracts the entity from the rejected payload, fetches the authorized version
 * from the API, computes differences, and returns a comparison result.
 *
 * @param aggregator The aggregator service for API access
 * @param rejectedPayload The payload from the invalid content update error event
 * @return EntityComparisonResult containing the comparison data
 * @throws IllegalArgumentException if the rejected payload doesn't contain valid entity data
 * @throws Exception if the API call fails
 */
suspend fun compareRejectedWithAuthorized(
    aggregator: AggregatorService,
    rejectedPayload: JsonObject
): EntityComparisonResult {
    // Extract entity from rejected payload
    // For full updates, it's in "entity" field
    // For partial updates, it's in "modification" field
    val rejectedEntity = (rejectedPayload["entity"] ?: rejectedPayload["modification"]) as? JsonObject
        ?: throw IllegalArgumentException("Unable to extract entity from rejected payload")

    // Extract entity information
    val entityId = rejectedEntity.entityId
        ?: throw IllegalArgumentException("Entity missing 'id' field")
    val entityType = rejectedEntity.entityType
        ?: throw IllegalArgumentException("Entity missing 'type' field")
    val entityVersion = rejectedEntity.entityVersion
        ?: throw IllegalArgumentException("Entity missing 'version' field")

    // Fetch the authorized version by entity ID (all versions), then find the one
    // matching the entity type. This avoids depending on the rejected entity's version
    // field, which may be incorrect (the reason it was rejected in the first place).
    val authorizedEntity = try {
        aggregator.clientApi.getOutgoingEntity(entityId)
            .filterIsInstance<JsonObject>()
            .firstOrNull { it.entityType == entityType }
    } catch (e: Exception) {
        if (e.message?.contains("404") == true || e.message?.contains("Not Found") == true) {
            null
        } else {
            throw e
        }
    }

    // When no authorized version exists, fetch a valid reference entity of the same type
    // so the user can compare the rejected payload against a known-good example.
    val referenceEntity = if (authorizedEntity == null) {
        try {
            aggregator.clientApi.searchEntities(types = entityType, size = 1)
                .entities.firstOrNull() as? JsonObject
        } catch (_: Exception) {
            null
        }
    } else {
        null
    }

    // Compute differences between authorized (or reference) and rejected
    // Direction: authorized/reference (old) -> rejected (new)
    val compareWith = authorizedEntity ?: referenceEntity
    val diffs = if (compareWith != null) {
        diffJson("", compareWith, rejectedEntity).map { diff ->
            diff.copy(path = diff.path.split(".").joinToString(" > ") { formatFieldName(it) })
        }
    } else {
        emptyList()
    }

    return EntityComparisonResult(
        rejected = rejectedEntity,
        authorized = authorizedEntity,
        referenceEntity = referenceEntity,
        entityId = entityId,
        entityType = entityType,
        entityVersion = entityVersion,
        diffs = diffs
    )
}

private val lenientJson = Json { ignoreUnknownKeys = true }

/**
 * Compares user-provided JSON from the validator against a valid entity of the same type.
 *
 * Extracts the entity from the JSON input (handling CloudEvent envelopes and content update
 * wrappers), fetches the authorized version by entity ID if available, and falls back to
 * a reference entity search by type. Returns a comparison result with computed diffs.
 *
 * @param aggregator The aggregator service for API access
 * @param jsonInput The raw JSON string from the validation panel
 * @param entityTypeName The entity type name from the page context (e.g., "interactive-free-form")
 * @return EntityComparisonResult containing the comparison data
 */
suspend fun compareJsonWithReference(
    aggregator: AggregatorService,
    jsonInput: String,
    entityTypeName: String
): EntityComparisonResult {
    val parsed = lenientJson.parseToJsonElement(jsonInput).jsonObject

    // Extract entity from possible wrappers:
    // CloudEvent: { "data": { "entity": {...} } }
    // Content update: { "entity": {...} }
    // Direct entity: { "id": "...", "type": "...", ... }
    val entity = (parsed["data"] as? JsonObject)?.let { data ->
        (data["entity"] as? JsonObject) ?: data
    } ?: (parsed["entity"] as? JsonObject) ?: parsed

    val entityId = entity.entityId
    val entityVersion = entity.entityVersion ?: "unknown"

    // Try to fetch the authorized version by entity ID
    val authorizedEntity = if (entityId != null) {
        try {
            aggregator.clientApi.getOutgoingEntity(entityId)
                .filterIsInstance<JsonObject>()
                .firstOrNull { it.entityType == entityTypeName }
        } catch (_: Exception) {
            null
        }
    } else {
        null
    }

    // Fall back to a reference entity of the same type
    val referenceEntity = if (authorizedEntity == null) {
        try {
            aggregator.clientApi.searchEntities(types = entityTypeName, size = 1)
                .entities.firstOrNull() as? JsonObject
        } catch (_: Exception) {
            null
        }
    } else {
        null
    }

    val compareWith = authorizedEntity ?: referenceEntity
    val diffs = if (compareWith != null) {
        diffJson("", compareWith, entity).map { diff ->
            diff.copy(path = diff.path.split(".").joinToString(" > ") { formatFieldName(it) })
        }
    } else {
        emptyList()
    }

    return EntityComparisonResult(
        rejected = entity,
        authorized = authorizedEntity,
        referenceEntity = referenceEntity,
        entityId = entityId ?: "unknown",
        entityType = entityTypeName,
        entityVersion = entityVersion,
        diffs = diffs
    )
}
